import { createContext, useContext, useEffect, useState } from "react";
import api, { setPreviewReadOnly } from "@/lib/api";
import { roleForPlan, PLAN_LABEL } from "@/lib/plans";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(false);
  const [previewTier] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("preview");
    if (q && PLAN_LABEL[q]) sessionStorage.setItem("previewTier", q);
    const stored = sessionStorage.getItem("previewTier");
    return stored && PLAN_LABEL[stored] ? stored : null;
  });

  useEffect(() => {
    // Returning from Google OAuth: let AuthCallback exchange the session_id first.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    const token = localStorage.getItem("ag_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => {
        const real = r.data;
        // Preview is admin-only and purely a visual/read-only simulation.
        if (previewTier && real.role === "admin") {
          setPreviewReadOnly(true);
          setPreview(true);
          setUser({ ...real, plan: previewTier, role: roleForPlan(previewTier) });
        } else {
          if (previewTier) sessionStorage.removeItem("previewTier");
          setUser(real);
        }
      })
      .catch(() => localStorage.removeItem("ag_token"))
      .finally(() => setLoading(false));
  }, [previewTier]);

  const exitPreview = () => {
    sessionStorage.removeItem("previewTier");
    try { window.close(); } catch (e) { /* opened manually */ }
    window.location.href = "/admin";
  };

  const persist = (data) => {
    localStorage.setItem("ag_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const applyGoogleSession = (data) => persist(data);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return persist(data);
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return persist(data);
  };

  const logout = () => {
    localStorage.removeItem("ag_token");
    setUser(null);
  };

  const updateUser = (patch) => setUser((u) => ({ ...u, ...patch }));

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, applyGoogleSession, preview, previewTier, previewLabel: PLAN_LABEL[previewTier], exitPreview }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
